import { isPos } from 'src/config'
import { DELIVERY_TYPE_OPTIONS } from 'src/models/delivery-type/DeliveryType'
import { ORDER_ITEM_TYPES } from 'src/models/order-item/OrderItem'
import itemDeliveryTypeOptionsComputer from 'src/services/item-delivery/ItemDeliveryTypeOptionsComputer'

/**
 * Computer that computes available Delivery Type, Location, and Position options for a given OrderItem.
 * Location and Position options will include potential options for Theater Seat Delivery if an
 * inapplicable Delivery Type is selected, to determine whether Theater Seat is a valid Delivery Type.
 */
class OrderItemDeliveryOptionsComputer {
  static #instance = null

  constructor () {
    if (!OrderItemDeliveryOptionsComputer.#instance) {
      OrderItemDeliveryOptionsComputer.#instance = this
    }
    return OrderItemDeliveryOptionsComputer.#instance
  }

  deliveryLocationOptions (orderItem) {
    if (_.isNil(orderItem.type) || orderItem.virtual) return []

    if (_.isEmpty(orderItem.configuration?.deliveryLocations?.data)) return []

    const deliveryType = orderItem.deliveryTypeForPlacing || orderItem.defaultDeliveryType
    if (_.isNil(deliveryType) || !_.includes(DELIVERY_TYPE_OPTIONS.DELIVERY_LOCATION_REQUIRED, deliveryType)) return []

    const allDeliveryLocationOptions = _.sortBy(_.filter(orderItem.configuration.deliveryLocations.data, { type: deliveryType }), 'displayOrder')

    if (deliveryType === DELIVERY_TYPE_OPTIONS.THEATER_SEAT) {
      if (isPos && _.isEmpty(orderItem.order.futureShowings)) return allDeliveryLocationOptions

      return _(orderItem.order.futureShowings).map(showing => {
        return _.find(allDeliveryLocationOptions, { screenId: showing.screenId })
      }).compact().value()
    }

    return allDeliveryLocationOptions
  }

  deliveryPositionOptions (orderItem) {
    if (_.isNil(orderItem.type) || orderItem.virtual) return []

    if (_.isEmpty(orderItem.configuration?.deliveryLocations?.data)) return []

    const deliveryType = orderItem.deliveryTypeForPlacing || orderItem.defaultDeliveryType

    const deliveryLocationName = orderItem.deliveryLocation || orderItem.order.deliveryLocation
    if (_.isNil(deliveryLocationName)) return []

    const deliveryLocation = _.find(orderItem.configuration.deliveryLocations.data, { name: deliveryLocationName, type: deliveryType })
    if (_.isNil(deliveryLocation)) return []

    const allDeliveryPositionOptions = _.uniq(deliveryLocation.positions)

    if (deliveryType === DELIVERY_TYPE_OPTIONS.THEATER_SEAT) {
      const showings = _.filter(orderItem.order.allShowings, { screenId: deliveryLocation.screenId })
      const allSelectedSeats = _(showings).map(showing => orderItem.order.allSelectedSeats(showing)).flatten().value()

      if (isPos && _.isEmpty(allSelectedSeats)) return allDeliveryPositionOptions

      return _.filter(allDeliveryPositionOptions, deliveryPosition => {
        const rawDeliveryPosition = deliveryPosition.replace(deliveryLocation.positionsPrefix, '')
        return _.includes(allSelectedSeats, rawDeliveryPosition)
      })
    }

    return allDeliveryPositionOptions
  }

  deliveryTypeOptions (orderItem) {
    if (_.isNil(orderItem.type)) return []

    if (orderItem.virtual) {
      return [{
        value: DELIVERY_TYPE_OPTIONS.VIRTUAL,
        label: DELIVERY_TYPE_OPTIONS.VIRTUAL
      }]
    }

    if (orderItem.type !== ORDER_ITEM_TYPES.ITEM) {
      throw new Error(`Unhandled order item type ${orderItem.type}`)
    }

    return itemDeliveryTypeOptionsComputer.deliveryTypeOptions(orderItem.item, orderItem.order)
  }
}

const orderItemDeliveryOptionsComputer = new OrderItemDeliveryOptionsComputer()
export default orderItemDeliveryOptionsComputer
